import { useEffect, useState } from "react";
import TopBar from "@/components/custom/TopBar";
import i18n from "@/lang/i18n";
import PageLayout from "@/components/custom/PageLayout";
import { DateTime } from "luxon";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/themeConfigs/ThemeContext";
import { MonthDropdown } from "./CarsAllocatedChart/MonthDropDown";
import { CarsAllocatedComparisonChart } from "./CarsAllocatedChart/CarsAllocatedComparisonChart";
import CarsAllocatedTable from "./CarsAllocatedTable";
import { CarStatusDashboardAllotmentDetailsResponse } from "@/central-atoms/everest-fleet-pwa/responses";
import DashboardService from "@/services/api/DashboardService";
import { CarDetailsTable } from "@/central-atoms/everest-fleet-pwa/types";
import { BottomNav } from "@/components/custom/BottomNav";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useHubDetails } from "@/redux/hooks/useSupervisorDetails";

export default function CarsAllocated() {
  const { theme } = useTheme();
  const [view, setView] = useState("table"); // default view
  const [currentDateTime, setCurrentDateTime] = useState(
    DateTime.now().toFormat("dd LLLL, hh:mm a")
  );
  const hubDetails = useHubDetails();
  const [carAllotmentDetails, setCarAllotmentDetails] =
    useState<CarStatusDashboardAllotmentDetailsResponse | null>(null);

  const carsAllottedToday = useSelector(
    (state: RootState) => state.carStatus.car_status?.cars_allotted_today
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(DateTime.now().toFormat("dd LLLL, hh:mm a"));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await DashboardService.getCarStatusDashboardDetails(
          hubDetails?.name,
          "alloted_cars"
        );
        setCarAllotmentDetails(data);
      } catch (error) {
        console.error("Failed to fetch car allocation details:", error);
      }
    };
    fetchData();
  }, []);

  const carTableData: CarDetailsTable[] = carAllotmentDetails
    ? carAllotmentDetails.data.data.vehicle_details.map((vehicle) => ({
        ...vehicle,
        vehicle_number: vehicle.car_number|| "N/A", // Provide default value if missing
        phone: vehicle.phone || "N/A", // Provide default value if missing
      }))
    : [];

  const chartData = carAllotmentDetails
    ? [
        {
          week: "Week 1",
          vehicleCount:
            carAllotmentDetails.data.data.allotment_comparison.week_1,
        },
        {
          week: "Week 2",
          vehicleCount:
            carAllotmentDetails.data.data.allotment_comparison.week_2,
        },
        {
          week: "Week 3",
          vehicleCount:
            carAllotmentDetails.data.data.allotment_comparison.week_3,
        },
        {
          week: "Week 4",
          vehicleCount:
            carAllotmentDetails.data.data.allotment_comparison.week_4,
        },
        {
          week: "Week 5",
          vehicleCount:
            carAllotmentDetails.data.data.allotment_comparison.week_5,
        },
      ]
    : [];

  return (
    <>
      <PageLayout>
        <TopBar
          color={theme.colors.secondary}
          text={i18n.t("carsAllocated")}
          number={carsAllottedToday ? carsAllottedToday.toString() : "0"}
        />
        <br />
        {view === "chart" ? (
          <>
            <CarsAllocatedComparisonChart data={chartData} />
            <div className="mt-5 flex justify-end items-center">
              <Button
                variant="link"
                className="font-bold"
                onClick={() => setView("table")}
              >
                Table View
              </Button>
            </div>
          </>
        ) : (
          <>
            <CarsAllocatedTable data={carTableData} />
            <div className="mt-5 flex justify-between items-center">
              <p className="text-muted-foreground">As of {currentDateTime}</p>
              <Button
                variant="link"
                className="font-bold"
                onClick={() => setView("chart")}
              >
                Chart View
              </Button>
            </div>
          </>
        )}
      </PageLayout>
      <BottomNav active={1} />
    </>
  );
}
